import type { CSSProperties } from 'react';

import { getLinkAriaLabel } from '@/utils/urlUtils';

import { Heading } from '@/components/Cms/Heading';
import { MediaWrapper } from '@/components/Cms/MediaWrapper';
import LinkWithLocale from '@/components/LinkWithLocale/LinkWithLocale';
import type {
  ContentfulHeadingFragment,
  ContentfulImageWithFocalPointFragment,
} from '@/graphql/contentful/api';
import { useRegionProvider } from '@/providers/RegionProvider';
import { theme } from '@/theme';

import type { CardProps } from '../../types';
import { FormatSubheading } from '../CardContent';
import { Cta } from '../CTA';

import * as S from './ImageTeaser.styles';

export const ImageTeaser = (props: CardProps) => {
  const { locale } = useRegionProvider();
  const hasMedia = !!(
    props.media?.image?.url ??
    props.media?.imageMobile?.url ??
    props.media?.video?.url ??
    props.media?.videoMobile?.url
  );
  const headType = props.placeholderHeading?.headingType;
  return (
    <S.Wrapper
      key={props?.sys.id}
      $visibleInDesktop={props.visibleInDesktop}
      $visibleInMobile={props.visibleInMobile}
      className={`${props.className} ImageTeaser`}
      data-test-id='ImageTeaserWrapper'
    >
      {hasMedia && (
        <S.ImageTeaserBox
          {...(props?.url && {
            'aria-label': getLinkAriaLabel(props.url, locale.code),
          })}
          as={props?.url ? LinkWithLocale : 'div'}
          className='ImageTeaserBox'
          {...(props?.url && { href: props?.url })}
          data-test-id='ImageTeaserBox'
        >
          <MediaWrapper
            {...(props.media as ContentfulImageWithFocalPointFragment)}
            aspectRatio={props.aspectRatio}
            data-test-id='ImageTeaserMedia'
            mediaWrapperType={props.mediaWrapperType}
            preload={props.preload}
            sizes='(max-width: 991px) 80vw, 33vw'
            url={props?.url ? null : props.media?.url} //  nested link will throw Hydration failed error, and when S.ImageTeaserBox is a Link, and the mediaWrapper is also a Link(like <Link><Link>xxxxxx</Link></Link>, the inside link is obviously useless,so make the MediaWrapper not a Link
          />
        </S.ImageTeaserBox>
      )}
      <S.ImageTeaserContent
        alignItems='center'
        data-test-id='ImageTeaserContent'
        flexDirection='column'
        justifyContent='center'
      >
        <Heading
          Heading={props?.placeholderHeading as ContentfulHeadingFragment}
        />
        <S.SubHeadingBox
          $color={props?.textColor ?? theme.colors.default}
          data-test-id='ImageTeaserSubHeadingBox'
          headingType={headType ?? 'h2'}
          textAlign={props.textAlignment as CSSProperties['textAlign']}
        >
          <FormatSubheading subHeading={props?.subHeading ?? ''} />
        </S.SubHeadingBox>
      </S.ImageTeaserContent>

      <S.ImageCtaWarpper data-test-id='ImageTeaserCtaWrapper'>
        <Cta
          data-test-id='ImageTeaserCta'
          onClickCta={props.onClickCta}
          openUrlInNewTab={props?.openUrlInNewTab}
          url={props?.url ?? '#'}
          urlText={props?.urlText}
          urlType={props?.urlType}
        />
      </S.ImageCtaWarpper>
    </S.Wrapper>
  );
};

ImageTeaser.displayName = 'ImageTeaser';
